import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CheckCircle2, Clock, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const ordinal = (d) => {
  const s = d % 10 === 1 && d !== 11 ? "st" : d % 10 === 2 && d !== 12 ? "nd" : d % 10 === 3 && d !== 13 ? "rd" : "th";
  return `${d}${s}`;
};

const useFixedBills = () => useQuery({ queryKey: ["fixed-bills"], queryFn: async () => (await api.get("/fixed-bills")).data });

const refresh = (qc) => ["fixed-bills", "dashboard", "expenses"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

export const FixedBillsCard = ({ isAdmin, memberCount }) => {
  const qc = useQueryClient();
  const { data } = useFixedBills();
  const [busyId, setBusyId] = useState(null);
  const bills = data?.bills || [];
  if (!bills.length) return null;

  const addToMonth = async (b) => {
    setBusyId(b.id);
    try {
      await api.post(`/fixed-bills/${b.id}/add`);
      refresh(qc);
      toast.success(`${b.name} added for this month & split`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add bill");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-card p-5" data-testid="fixed-bills-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-heading font-bold flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Fixed monthly bills</h2>
        <div className="text-right">
          <div className="font-heading font-bold tabular-nums text-sm" data-testid="fixed-bills-total">{money(data.total)}/mo</div>
          {memberCount > 0 && <div className="text-[11px] text-muted-foreground">≈ {money(data.total / memberCount)} each</div>}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {bills.map((b) => (
          <div key={b.id} className="rounded-2xl bg-secondary/50 p-3 flex items-center gap-3" data-testid="fixed-bill-item">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{b.name}</div>
              <div className="text-[11px] text-muted-foreground">{b.category} · due {ordinal(b.due_day)}</div>
              {b.added ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 mt-0.5" data-testid="fixed-bill-added"><CheckCircle2 className="h-3 w-3" /> Added this month</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 mt-0.5" data-testid="fixed-bill-not-added"><Clock className="h-3 w-3" /> Not added yet</span>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="font-heading font-bold tabular-nums text-sm">{money(b.amount)}</div>
              {isAdmin && !b.added && (
                <Button size="sm" variant="outline" className="h-7 mt-1 rounded-full text-[11px] px-2.5" onClick={() => addToMonth(b)} disabled={busyId === b.id} data-testid="fixed-bill-add-btn">
                  {busyId === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add now"}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FixedBillsManager = ({ categories }) => {
  const qc = useQueryClient();
  const { data } = useFixedBills();
  const bills = data?.bills || [];
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [dueDay, setDueDay] = useState("1");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const amt = parseFloat(amount);
    if (!name.trim()) return toast.error("Enter a bill name");
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/fixed-bills", { name: name.trim(), amount: amt, category: category || categories[0] || "Other", due_day: Number(dueDay) });
      refresh(qc);
      setName("");
      setAmount("");
      toast.success("Fixed bill saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save bill");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/fixed-bills/${b.id}`);
      refresh(qc);
      toast.success(`${b.name} removed from fixed bills`);
    } catch {
      toast.error("Could not remove bill");
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-card p-5" data-testid="fixed-bills-manager">
      <div className="flex items-center gap-2 mb-1"><CalendarClock className="h-4 w-4 text-primary" /><h2 className="font-heading font-bold">Fixed monthly bills</h2></div>
      <p className="text-xs text-muted-foreground mb-3">Rent, electricity, Wi-Fi and other bills that repeat every month. They always show on everyone's home page, and you can add each one to the month with one tap.</p>
      <div className="space-y-2 mb-3">
        {bills.map((b) => (
          <div key={b.id} className="flex items-center gap-3 rounded-xl bg-secondary/50 p-3" data-testid="fixed-bill-row">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{b.name}</div>
              <div className="text-[11px] text-muted-foreground">{b.category} · due {ordinal(b.due_day)}</div>
            </div>
            <div className="font-heading font-bold tabular-nums text-sm">{money(b.amount)}</div>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(b)} data-testid="fixed-bill-delete-btn"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-dashed border-border p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rent" className="h-10 rounded-lg col-span-2 sm:col-span-1" data-testid="fixed-bill-name-input" />
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" inputMode="decimal" placeholder="Amount" className="h-10 rounded-lg font-mono col-span-2 sm:col-span-1" data-testid="fixed-bill-amount-input" />
          <Select value={category || categories[0] || ""} onValueChange={setCategory}>
            <SelectTrigger className="h-10 rounded-lg" data-testid="fixed-bill-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={dueDay} onValueChange={setDueDay}>
            <SelectTrigger className="h-10 rounded-lg" data-testid="fixed-bill-due-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">{Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <SelectItem key={d} value={String(d)}>Due {ordinal(d)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={add} disabled={busy} className="w-full h-10 rounded-lg" data-testid="fixed-bill-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add fixed bill</>}</Button>
      </div>
    </div>
  );
};
